/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	createContext,
	type ReactNode,
	startTransition,
	useCallback,
	useContext,
	useMemo,
	useState,
} from 'react';

import { jsx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Subscriber } from '@atlaskit/rovo-triggers/main';
import { CUSTOM_SKILL_DELETED_EVENT } from '@atlaskit/rovo-triggers/types';

import { clearSkillsCache } from '../insert-menu-provider/fetch-skills';

/**
 * Configuration for Rovo Skills features.
 * This type is designed to be extensible for future configuration needs.
 */
export type RovoSkillsConfig = {
	/**
	 * Tags identifying sets of skills and conversation actions.
	 * Used to fetch skill metadata from the backend.
	 */
	tags?: {
		skillRegistryTags?: string[];
	};
};

/**
 * Context for Rovo Skills configuration.
 */
const RovoSkillsCloudIdContext = createContext<string | undefined>(undefined);

const RovoSkillsConfigContext = createContext<RovoSkillsConfig | undefined>(undefined);

export type RovoSkillsInvalidation = {
	skillsRevision: number;
	/** Urgent browse invalidation blocks pagination before metadata transitions finish. */
	browseSkillsRevision?: number;
	/** Successful deletions retained across search, pagination and browse remounts. */
	deletedSkills?: ReadonlyArray<{ cloudId: string; skillAri: string }>;
	invalidateSkillLibrary: () => void;
};

const fallbackInvalidation: RovoSkillsInvalidation = {
	skillsRevision: 0,
	// Outside RovoSkillsConfigProvider there is no revision state to bump, so this only clears
	// the vanilla insert-menu cache. Relay consumers need the provider to refetch by revision.
	invalidateSkillLibrary: clearSkillsCache,
};

const RovoSkillsInvalidationContext = createContext<RovoSkillsInvalidation>(fallbackInvalidation);

/**
 * Hook to access Rovo Skills configuration.
 *
 * @returns The Rovo Skills configuration, or undefined if used outside of RovoSkillsConfigProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const config = useRovoSkillsConfig();
 *   const tags = config?.tags;
 * }
 * ```
 */
export function useRovoSkillsConfig(): RovoSkillsConfig | undefined {
	return useContext(RovoSkillsConfigContext);
}

export function useRovoSkillsInvalidation(): RovoSkillsInvalidation {
	return useContext(RovoSkillsInvalidationContext);
}

type RovoSkillsConfigProviderProps = {
	/** Site whose skill deletion events should refresh this library. Inherited by nested providers. */
	cloudId?: string;
	/**
	 * Configuration for Rovo Skills features.
	 */
	config: RovoSkillsConfig;

	/**
	 * Child components that will have access to the configuration.
	 */
	children: ReactNode;
};

/**
 * Provider component that supplies Rovo Skills configuration to descendant components.
 *
 * This provider should be placed high in the component tree (e.g., at the AISmartButton level)
 * to make configuration available to all skill-related components.
 *
 * @example
 * ```tsx
 * <RovoSkillsConfigProvider config={{ tags }}>
 *   <YourComponents />
 * </RovoSkillsConfigProvider>
 * ```
 */
export function RovoSkillsConfigProvider({
	cloudId,
	config,
	children,
}: RovoSkillsConfigProviderProps): ReactNode {
	const parentCloudId = useContext(RovoSkillsCloudIdContext);
	const resolvedCloudId = cloudId ?? parentCloudId;
	const value = useMemo(() => config, [config]);
	const [skillsRevision, setSkillsRevision] = useState(0);
	const [browseSkillsRevision, setBrowseSkillsRevision] = useState(0);
	const [deletedSkills, setDeletedSkills] = useState<
		ReadonlyArray<{ cloudId: string; skillAri: string }>
	>([]);
	const invalidateSkillLibrary = useCallback(() => {
		clearSkillsCache();
		if (fg('rovo_chat_skills_background_refresh')) {
			setBrowseSkillsRevision((revision) => revision + 1);
		}
		startTransition(() => {
			setSkillsRevision((revision) => revision + 1);
		});
	}, []);

	const invalidateDeletedSkill = useCallback(
		(cloudId: string, skillAri: string) => {
			if (fg('rovo_chat_skills_background_refresh')) {
				setDeletedSkills((previous) =>
					previous.some((skill) => skill.cloudId === cloudId && skill.skillAri === skillAri)
						? previous
						: [...previous, { cloudId, skillAri }],
				);
			}
			invalidateSkillLibrary();
		},
		[invalidateSkillLibrary],
	);

	const invalidationValue = useMemo(
		() => ({
			skillsRevision,
			browseSkillsRevision,
			deletedSkills,
			invalidateSkillLibrary,
		}),
		[browseSkillsRevision, deletedSkills, invalidateSkillLibrary, skillsRevision],
	);

	return (
		<RovoSkillsCloudIdContext.Provider value={resolvedCloudId}>
			<RovoSkillsConfigContext.Provider value={value}>
				<RovoSkillsInvalidationContext.Provider value={invalidationValue}>
					{fg('rovo_chat_custom_skill_actions') && (
						<Subscriber
							topic="rovo-skills"
							onEvent={(payload) => {
								if (
									payload.type === CUSTOM_SKILL_DELETED_EVENT &&
									payload.data.cloudId === resolvedCloudId
								) {
									invalidateDeletedSkill(payload.data.cloudId, payload.data.skillAri);
								}
							}}
						/>
					)}
					{children}
				</RovoSkillsInvalidationContext.Provider>
			</RovoSkillsConfigContext.Provider>
		</RovoSkillsCloudIdContext.Provider>
	);
}
